<template>
<div class="rdv-page">
  <div class="hw-card">
    <div class="rdv-header">
      <p class="rdv-intro">Durée estimée</p>
      <div class="duration-badge">{{ writeMinutes(duration) }}</div>
      <h1 class="hw-title" style="margin-top: 0.75rem;">Choisissez un créneau</h1>
    </div>

    <div class="loader-area" v-show="loading">
      <GridLoader :color="'#3b82f6'" />
    </div>

    <DialogView v-model:visible="error">
      <h1>Aïe...</h1>
      <p>Une erreur est survenue. Quelqu'un a peut-être réservé entre temps.<br/>Veuillez réessayer ! En cas de problème, vous pouvez me joindre au <a href="tel:0782381360">078 238 13 60</a></p>
      <InputButton label="Compris !" icon="pi pi-check" @click="closeAndRetry" autofocus />
    </DialogView>

    <div class="week-controls" v-show="!loading && !error">
      <InputButton
        icon="pi pi-chevron-left"
        class="p-button-text p-button-rounded"
        :disabled="!canGoPreviousWeek"
        @click="shiftWeek(-1)"
      />
      <div class="week-label">{{ currentWeekLabel }}</div>
      <InputButton icon="pi pi-chevron-right" class="p-button-text p-button-rounded" @click="shiftWeek(1)" />
      <CalendarView
        class="week-picker"
        :minDate="mindate"
        :maxDate="maxdate"
        v-model="dateViewed"
        :manualInput="false"
        :showIcon="true"
        :touchUI="true"
        @date-select="updateApps()"
      />
    </div>

    <div v-show="!loading && !error">
      <div class="day-tabs" v-if="dayEntries.length > 0">
        <button
          v-for="day in dayEntries"
          :key="day.key"
          type="button"
          class="day-tab"
          :class="{ active: selectedDayKey === day.key, empty: day.appointments.length === 0 }"
          @click="selectDay(day.key)"
        >
          <span class="day-tab-title">{{ day.shortLabel }}</span>
          <span v-if="day.appointments.length === 0" class="day-tab-status unavailable">Non disponible</span>
          <span v-else-if="day.appointments.length < 10" class="day-tab-status last-places">Dernières places !</span>
        </button>
      </div>

      <div class="slot-section" v-if="selectedDay">
        <h4 class="slot-day-title">{{ selectedDay.longLabel }}</h4>
        <div class="slot-grid" v-if="selectedDay.appointments.length > 0">
          <button
            v-for="(rdv, index) in selectedDay.appointments"
            :key="`${selectedDay.key}-${rdv}-${index}`"
            type="button"
            class="slot-btn"
            :class="{ selected: selectedSlotIndex === index }"
            @click="selectSlot(index)"
          >
            {{ rdv }}
          </button>
        </div>
        <p v-else class="empty-slots"><i>Il n'y a pas de rendez-vous disponible ce jour.</i></p>
      </div>
    </div>
  </div>

  <div class="hw-actions">
    <button class="hw-btn hw-btn-secondary" @click="previousPage()">
      <i class="pi pi-arrow-left"></i>
      Retour
    </button>
    <button
      v-show="selectedDate != ''"
      class="hw-btn hw-btn-success"
      :disabled="bookingInProgress"
      @click="book()"
    >
      <i class="pi pi-check-circle" v-if="!bookingInProgress"></i>
      <i class="pi pi-spin pi-spinner" v-if="bookingInProgress"></i>
      Réserver
    </button>
  </div>
</div>
</template>



<style scoped>
.rdv-page {
  text-align: left;
}

.rdv-header {
  text-align: center;
  margin-bottom: 1rem;
}

.rdv-intro {
  margin: 0;
  color: #64748b;
  font-size: 0.85rem;
}

.duration-badge {
  display: inline-block;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 10px;
  padding: 0.35rem 1rem;
  font-size: 1.3rem;
  font-weight: 700;
  color: #1d4ed8;
  margin-top: 0.25rem;
}

.loader-area {
  display: flex;
  justify-content: center;
  padding: 2rem 0;
}

.week-controls {
  display: grid;
  grid-template-columns: auto 1fr auto auto;
  gap: 0.45rem;
  align-items: center;
  margin: 0.8rem 0 1rem;
}

.week-label {
  text-align: center;
  font-weight: 600;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 0.55rem 0.7rem;
  color: #0f172a;
  font-size: 0.9rem;
}

.week-picker {
  max-width: 180px;
}

.day-tabs {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(110px, 1fr));
  gap: 0.5rem;
  margin-bottom: 0.8rem;
}

.day-tab {
  border: 2px solid #e2e8f0;
  background: #f8fafc;
  border-radius: 12px;
  padding: 0.6rem 0.75rem;
  text-align: left;
  cursor: pointer;
  transition: all 0.2s ease;
  -webkit-tap-highlight-color: transparent;
}

.day-tab:hover:not(.empty) {
  border-color: #93c5fd;
  background: #eff6ff;
}

.day-tab.active {
  border-color: #3b82f6;
  background: #eff6ff;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.12);
}

.day-tab.empty {
  border-color: #e2e8f0;
  background: #f1f5f9;
  color: #94a3b8;
  cursor: default;
}

.day-tab-title {
  display: block;
  font-weight: 700;
  font-size: 0.88rem;
}

.day-tab-status {
  display: block;
  margin-top: 0.15rem;
  font-size: 0.75rem;
  font-weight: 700;
}

.day-tab-status.last-places {
  color: #d97706;
}

.day-tab-status.unavailable {
  color: #94a3b8;
}

.slot-section {
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 1rem;
  background: #f8fafc;
}

.slot-day-title {
  margin: 0 0 0.7rem 0;
  font-size: 0.95rem;
  color: #374151;
}

.slot-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(88px, 1fr));
  gap: 0.5rem;
}

.slot-btn {
  border: 2px solid #e2e8f0;
  background: #fff;
  border-radius: 10px;
  padding: 0.6rem 0.3rem;
  font-weight: 600;
  font-size: 0.9rem;
  color: #1e293b;
  cursor: pointer;
  transition: all 0.15s ease;
  -webkit-tap-highlight-color: transparent;
}

.slot-btn:hover {
  border-color: #93c5fd;
  background: #eff6ff;
}

.slot-btn.selected {
  background: #3b82f6;
  border-color: #2563eb;
  color: #fff;
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
}

.empty-slots {
  margin: 0;
  color: #94a3b8;
  font-size: 0.9rem;
}

@media (max-width: 480px) {
  .week-controls {
    grid-template-columns: auto 1fr auto;
  }

  .week-picker {
    grid-column: 1 / -1;
    max-width: 100%;
  }

  .slot-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .day-tabs {
    grid-template-columns: repeat(auto-fit, minmax(95px, 1fr));
  }
}
</style>


<script setup>
import { ref, defineEmits, onMounted, computed, watch } from 'vue'

const emit = defineEmits(['previous-page', 'go-home', 'go-confirm']);

import { storeToRefs } from 'pinia'
import { useMainStore } from '../store'

import {getFrom, getTo, getTime } from  '../dateUtils.js'

import axios from 'axios'

const store = useMainStore();

var mindate = new Date();
var maxdate = new Date();
maxdate.setMonth(maxdate.getMonth() + 6);


const { duration, days, loading  } = storeToRefs(useMainStore())

 onMounted(() => {
    if(!(store.duration>0))
    {
        emit('go-home');
    }
});

const dateViewed = ref(new Date());

const selectedDate = ref("");
const bookingInProgress = ref(false);
const selectedDayKey = ref("");
const selectedSlotIndex = ref(-1);

const error = ref(false);


function previousPage() {
    emit('previous-page', {pageIndex: 3})
}

function writeMinutes(min) {
    var hours =  Math.floor(min/60);
    var minutes = min % 60;

    if(hours > 0 && minutes < 10)
    {
        return `${hours}h0${minutes}`
    }
    if(hours > 0)
    {
        return `${hours}h${minutes}`
    }
    return `${minutes} minutes`
}

const dayShortFormatter = new Intl.DateTimeFormat('fr-CH', { weekday: 'short', day: '2-digit', month: '2-digit' });
const dayLongFormatter = new Intl.DateTimeFormat('fr-CH', { weekday: 'long', day: '2-digit', month: 'long' });
const weekFormatter = new Intl.DateTimeFormat('fr-CH', { day: '2-digit', month: 'short' });

function startOfWeekMonday(date) {
  const d = new Date(date);
  const day = d.getDay() === 0 ? 7 : d.getDay();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - day + 1);
  return d;
}

const dayEntries = computed(() => {
    return Object.entries(days.value || {})
      .map(([key, values]) => {
        const isoDate = values?.datestring?.[0] || null;
        const dateObj = isoDate ? new Date(`${isoDate}T00:00:00`) : null;
        return {
          key,
          isoDate,
          dateObj,
          appointments: values?.appointments || [],
          shortLabel: dateObj ? dayShortFormatter.format(dateObj) : key,
          longLabel: dateObj ? dayLongFormatter.format(dateObj) : key
        };
      })
      .sort((a, b) => (a.isoDate || '').localeCompare(b.isoDate || ''));
});

const selectedDay = computed(() => dayEntries.value.find(day => day.key === selectedDayKey.value) || null);

const currentWeekLabel = computed(() => {
  const fromString = getFrom(new Date(dateViewed.value));
  const toString = getTo(new Date(dateViewed.value));
  const fromDate = new Date(`${fromString}T00:00:00`);
  const toDate = new Date(`${toString}T00:00:00`);
  return `Semaine du ${weekFormatter.format(fromDate)} au ${weekFormatter.format(toDate)}`;
});

const canGoPreviousWeek = computed(() => {
  const currentWeekStart = startOfWeekMonday(dateViewed.value).getTime();
  const minWeekStart = startOfWeekMonday(mindate).getTime();
  return currentWeekStart > minWeekStart;
});

watch(dayEntries, (entries) => {
  if (entries.length === 0) {
    selectedDayKey.value = "";
    selectedSlotIndex.value = -1;
    selectedDate.value = "";
    return;
  }

  if (!entries.some(day => day.key === selectedDayKey.value)) {
    const firstWithSlots = entries.find(day => day.appointments.length > 0);
    selectedDayKey.value = (firstWithSlots || entries[0]).key;
  }

  selectedSlotIndex.value = -1;
  selectedDate.value = "";
}, { immediate: true });

function selectDay(dayKey) {
  selectedDayKey.value = dayKey;
  selectedSlotIndex.value = -1;
  selectedDate.value = "";
}

function selectSlot(index) {
    if (!selectedDay.value || !selectedDay.value.isoDate) {
      return;
    }

    const time = selectedDay.value.appointments[index];
    selectedSlotIndex.value = index;
    selectedDate.value = `${selectedDay.value.isoDate}${getTime(time)}`;

    store.$patch((state) => {
      state.datestring = selectedDay.value.longLabel;
      state.timestring = time;
    });
}

function updateApps() {

    const passDate = new Date(dateViewed.value.getTime());
    
     store.$patch((state) => {
        state.loading = true;
        state.days = {};
      });

   axios
      .get(`${process.env.VUE_APP_API_URL}/Appointments?from=${getFrom(passDate)}&to=${getTo(passDate)}&duration=${store.duration}`)
      .then(response => {
          store.$patch((state) => {
            state.days = response.data;
            state.loading = false;
      });
      })
      .catch(() => {
        store.$patch((state) => {
          state.loading = false;
        });
      })
}

function shiftWeek(direction) {
  if (direction < 0 && !canGoPreviousWeek.value) {
    return;
  }

  const next = new Date(dateViewed.value.getTime());
  next.setDate(next.getDate() + (direction * 7));
  if (next < mindate) {
    return;
  } else if (next > maxdate) {
    dateViewed.value = new Date(maxdate);
  } else {
    dateViewed.value = next;
  }
  updateApps();
}


const lengthLookup = ref({
    'xs' : 'Très courts',
    's' : 'Courts',
    'm' : 'Mi-longs',
    'l': 'Longs', 
    'xl': 'Très longs'
}
);



const thicknessLookup = ref(
    {
    'fin' : 'Fins',
    'moyen' : 'Moyen', 
    'epais' : 'Epais', 
    'nsp' :  'Je ne sais pas'}
);

const detailLabelLookup = {
  coupeDetails: {
    coupeComplete: "Complète",
    pointes: "Pointes uniquement"
  },
  couleurDetails: {
    couleurComplete: "Complète",
    racines: "Racines uniquement"
  },
  mecheDetails: {
    mecheComplete: "Tête complète",
    partielles: "Mèches partielles"
  }
};
 

function book() {
    if (bookingInProgress.value || selectedDate.value === "") {
        return;
    }

    var clientData = store.clientData;
    const selectedServices = Array.isArray(store.services) ? store.services : [];
    const hasCoupe = selectedServices.includes("coupe");
    const hasCouleur = selectedServices.includes("couleur");
    const hasMeches = selectedServices.includes("mèches");
    const coupeDetailIsRelevant = hasCoupe && store.length !== "xs" && store.length !== "s";

    clientData.length = lengthLookup.value[store.length];
    clientData.thickness = thicknessLookup.value[store.thickness];
    clientData.services = selectedServices;
    clientData.finalLength = lengthLookup.value[store.finalLength] || store.finalLength;
    clientData.coupeDetails = coupeDetailIsRelevant ? (detailLabelLookup.coupeDetails[store.serviceDetails?.coupeDetails] || "") : "";
    clientData.couleurDetails = hasCouleur ? (detailLabelLookup.couleurDetails[store.serviceDetails?.couleurDetails] || "") : "";
    clientData.mecheDetails = hasMeches ? (detailLabelLookup.mecheDetails[store.serviceDetails?.mecheDetails] || "") : "";

    var json = JSON.stringify(clientData);

    const options = {
    headers: {"content-type": "application/json"}
}
        bookingInProgress.value = true;

        axios
      .post(`${process.env.VUE_APP_API_URL}/Appointments?appointmentTime=${selectedDate.value}&duration=${store.duration}`, json, options)
      .then(response => {
          console.log(response.status)
          bookingInProgress.value = false;
          emit('go-confirm');
      })
      .catch(reason => {
          bookingInProgress.value = false;
          console.log(reason);
          error.value = true;
      });
}

function closeAndRetry() {
    error.value = false;
    updateApps();
}

</script>
